import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchEventDetail } from './useEventDetail';
import { prefetchGroupShell, prefetchDmConversation } from '../../api/chats';
import { queryKeys } from '../../lib/query/queryKeys';
import { STALE } from '../../lib/query/staleTimes';
import { useNetworkStatus } from '../useNetworkStatus';

/** Prefetch event detail when a card enters the viewport (reachable networks only). */
export function usePrefetchEventOnVisible(eventId, ref) {
  const queryClient = useQueryClient();
  const { prefetchAllowed } = useNetworkStatus();
  const prefetchAllowedRef = useRef(prefetchAllowed);
  prefetchAllowedRef.current = prefetchAllowed;
  const isIntersectingRef = useRef(false);

  useEffect(() => {
    if (!eventId || !ref?.current) return undefined;
    const el = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        isIntersectingRef.current = entries[0]?.isIntersecting ?? false;
        if (isIntersectingRef.current && prefetchAllowedRef.current) {
          prefetchEventDetail(queryClient, eventId);
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [eventId, queryClient, ref]);

  useEffect(() => {
    if (prefetchAllowed && isIntersectingRef.current && eventId) {
      prefetchEventDetail(queryClient, eventId);
    }
  }, [prefetchAllowed, eventId, queryClient]);
}

/** Prefetch recent chat thread shells from the inbox list (reachable networks only). */
export function usePrefetchRecentChats(userChats) {
  const queryClient = useQueryClient();
  const { prefetchAllowed } = useNetworkStatus();

  useEffect(() => {
    if (!prefetchAllowed || !userChats?.length) return;
    const recent = userChats.slice(0, 5);
    recent.forEach((row) => {
      if (row.chat_kind === 'group' && row.group_id) {
        queryClient.prefetchQuery({
          queryKey: queryKeys.chats.group(row.group_id),
          queryFn: () => prefetchGroupShell(row.group_id),
          staleTime: STALE.inbox,
        });
      } else if (row.chat_kind === 'dm' && row.dm_conversation_id) {
        queryClient.prefetchQuery({
          queryKey: queryKeys.chats.dm(row.dm_conversation_id),
          queryFn: () => prefetchDmConversation(row.dm_conversation_id),
          staleTime: STALE.inbox,
        });
      }
    });
  }, [userChats, queryClient, prefetchAllowed]);
}
